import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  makeStyles,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  TextFields as TextIcon,
  PhotoCamera as PhotoCameraIcon,
  CheckCircle as CheckIcon,
} from "@material-ui/icons";
import { toast } from "react-toastify";
import { sendTextStatus, sendImageStatus, sendVideoStatus } from "../../services/status";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: theme.spacing(2),
  },
  tabPanel: {
    padding: theme.spacing(3, 0),
    minHeight: 300,
  },
  previewContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: "center",
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: 300,
    borderRadius: theme.shape.borderRadius,
  },
  previewVideo: {
    maxWidth: "100%",
    maxHeight: 300,
    borderRadius: theme.shape.borderRadius,
  },
  uploadButton: {
    marginTop: theme.spacing(2),
  },
  textPreview: {
    padding: theme.spacing(3),
    borderRadius: theme.shape.borderRadius,
    color: "#fff",
    minHeight: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    wordBreak: "break-word",
    fontFamily: (props) => getFontFamily(props.font),
  },
  colorPicker: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const getFontFamily = (font) => {
  const fonts = [
    "'Helvetica Neue', sans-serif",
    "'Courier New', monospace",
    "'Georgia', serif",
    "'Comic Sans MS', cursive",
    "'Impact', fantasy",
    "'Times New Roman', serif",
  ];
  return fonts[font] || fonts[0];
};

// Componente interno para exibir QR Code
const QRCodeContent = ({ whatsappId, onSuccess }) => {
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const fetchQRCode = async () => {
      try {
        console.log("[QRCodeContent] Buscando QR Code para whatsappId:", whatsappId);
        setLoading(true);
        const { data } = await api.get(`/puppeteer-status/${whatsappId}/qr`);
        console.log("[QRCodeContent] Resposta do QR Code:", data);

        if (data.authenticated) {
          console.log("[QRCodeContent] Já está autenticado! Chamando onSuccess em 1.5s");
          setAuthenticated(true);
          setTimeout(() => {
            if (onSuccess) onSuccess();
          }, 1500);
        } else {
          console.log("[QRCodeContent] Não autenticado, mostrando QR Code");
          setQrCode(data.qrCode);
        }
        setLoading(false);
      } catch (error) {
        console.error("[QRCodeContent] Erro ao obter QR Code:", error);
        setLoading(false);
      }
    };

    fetchQRCode();

    // Polling para verificar autenticação a cada 3 segundos
    console.log("[QRCodeContent] Iniciando polling de autenticação a cada 3s");
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/puppeteer-status/${whatsappId}/auth`);
        console.log("[QRCodeContent] Polling - autenticado?", data.authenticated);

        if (data.authenticated) {
          console.log("[QRCodeContent] ✅ Autenticado! Chamando onSuccess em 1.5s");
          setAuthenticated(true);
          clearInterval(interval);
          setTimeout(() => {
            if (onSuccess) onSuccess();
          }, 1500);
        }
      } catch (error) {
        console.error("[QRCodeContent] Erro ao verificar autenticação:", error);
      }
    }, 3000);

    return () => {
      console.log("[QRCodeContent] Limpando interval de polling");
      clearInterval(interval);
    };
  }, [whatsappId, onSuccess]);

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
        <Typography variant="body1" style={{ marginTop: 20 }}>
          Carregando QR Code...
        </Typography>
      </Box>
    );
  }

  if (authenticated) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={400}>
        <CheckIcon style={{ fontSize: 80, color: "#4caf50" }} />
        <Typography variant="h6" color="primary" style={{ marginTop: 16 }}>
          ✅ Autenticado com sucesso!
        </Typography>
        <Typography variant="caption" style={{ marginTop: 10 }}>
          Abrindo formulário...
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" padding={3} minHeight={400}>
      <Typography variant="body1" align="center" gutterBottom>
        <strong>Escaneie o QR Code abaixo com seu WhatsApp:</strong>
      </Typography>
      <Typography variant="caption" align="center" color="textSecondary" style={{ marginBottom: 16 }}>
        1. Abra o WhatsApp no celular<br />
        2. Toque em Menu (⋮) ou Configurações<br />
        3. Selecione "Aparelhos conectados"<br />
        4. Toque em "Conectar um aparelho"<br />
        5. Aponte a câmera para este QR Code
      </Typography>
      {qrCode && (
        <img
          src={qrCode}
          alt="QR Code"
          style={{ width: 300, height: 300, marginTop: 16, marginBottom: 16 }}
        />
      )}
      <Box display="flex" alignItems="center" marginTop={2}>
        <CircularProgress size={20} />
        <Typography variant="caption" color="textSecondary" style={{ marginLeft: 10 }}>
          Aguardando leitura do QR Code...
        </Typography>
      </Box>
    </Box>
  );
};

const StatusModal = ({ open, onClose, whatsappId }) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Estado para Status de Texto
  const [textContent, setTextContent] = useState("");
  const [backgroundColor, setBackgroundColor] = useState("#000000");
  const [font, setFont] = useState(0);

  // Estado para Imagem/Vídeo
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [caption, setCaption] = useState("");

  const classes = useStyles({ font });
  const fileInputRef = useRef(null);

  // Verificar autenticação quando o modal abrir
  React.useEffect(() => {
    const checkAuthentication = async () => {
      if (!open) return;

      console.log("[StatusModal] Modal aberto, verificando autenticação para whatsappId:", whatsappId);
      setCheckingAuth(true);
      try {
        const { data } = await api.get(`/puppeteer-status/${whatsappId}/auth`);
        console.log("[StatusModal] Resposta da verificação:", data);

        if (data.authenticated) {
          console.log("[StatusModal] ✅ Autenticado! Mostrando formulário de status");
          setIsAuthenticated(true);
          setShowQRModal(false);
        } else {
          console.log("[StatusModal] ❌ Não autenticado! Mostrando QR Code");
          setIsAuthenticated(false);
          setShowQRModal(true);
        }
      } catch (error) {
        console.error("[StatusModal] Erro ao verificar autenticação:", error);
        // Se der erro, assume que não está autenticado
        setIsAuthenticated(false);
        setShowQRModal(true);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuthentication();
  }, [open, whatsappId]);

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Limpar dados ao trocar de aba
    setMediaFile(null);
    setMediaPreview(null);
    setCaption("");
    setTextContent("");
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar tipo de arquivo
    const isImage = tabIndex === 1;
    const isVideo = tabIndex === 2;

    if (isImage && !file.type.startsWith("image/")) {
      toast.error("Por favor, selecione um arquivo de imagem");
      return;
    }

    if (isVideo && !file.type.startsWith("video/")) {
      toast.error("Por favor, selecione um arquivo de vídeo");
      return;
    }

    // Validar tamanho (max 16MB para imagem, 64MB para vídeo)
    const maxSize = isImage ? 16 * 1024 * 1024 : 64 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Arquivo muito grande. Máximo: ${isImage ? "16MB" : "64MB"}`);
      return;
    }

    setMediaFile(file);

    // Criar preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setMediaPreview(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleQRSuccess = () => {
    // Quando QR Code for escaneado com sucesso, fechar modal de QR e permitir envio
    console.log("[StatusModal] handleQRSuccess chamado! Mostrando formulário de status");
    setShowQRModal(false);
    setIsAuthenticated(true);
  };

  const handleSendStatus = async () => {
    setLoading(true);

    try {
      if (tabIndex === 0) {
        // Enviar status de texto
        if (!textContent.trim()) {
          toast.error("Digite algo para o status");
          setLoading(false);
          return;
        }

        await sendTextStatus(whatsappId, textContent, backgroundColor, font);
        toast.success("Status de texto enviado com sucesso!");
      } else if (tabIndex === 1) {
        // Enviar status com imagem
        if (!mediaFile) {
          toast.error("Selecione uma imagem");
          setLoading(false);
          return;
        }

        await sendImageStatus(whatsappId, mediaFile, caption);
        toast.success("Status com imagem enviado com sucesso!");
      } else if (tabIndex === 2) {
        // Enviar status com vídeo
        if (!mediaFile) {
          toast.error("Selecione um vídeo");
          setLoading(false);
          return;
        }

        await sendVideoStatus(whatsappId, mediaFile, caption);
        toast.success("Status com vídeo enviado com sucesso!");
      }

      // Limpar e fechar
      handleClose();
    } catch (error) {
      console.error("Erro ao enviar status:", error);

      // Se erro for de autenticação do Puppeteer, mostrar QR Code
      if (error.response?.data?.error?.includes("ERR_PUPPETEER_NOT_AUTHENTICATED")) {
        setShowQRModal(true);
        setIsAuthenticated(false);
      } else {
        toast.error(error.response?.data?.message || "Erro ao enviar status");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTextContent("");
    setBackgroundColor("#000000");
    setFont(0);
    setMediaFile(null);
    setMediaPreview(null);
    setCaption("");
    setTabIndex(0);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Typography variant="h6">
          {showQRModal ? "Autenticação WhatsApp" : "Enviar Status do WhatsApp"}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Estado 1: Verificando autenticação */}
        {checkingAuth ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress size={60} />
            <Typography variant="body1" style={{ marginTop: 20 }}>
              Verificando autenticação...
            </Typography>
          </Box>
        ) : showQRModal ? (
          /* Estado 2: Não autenticado - Mostrar QR Code */
          <QRCodeContent whatsappId={whatsappId} onSuccess={handleQRSuccess} />
        ) : (
          /* Estado 3: Autenticado - Mostrar formulário de status */
          <>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab icon={<TextIcon />} label="Texto" />
          <Tab icon={<ImageIcon />} label="Imagem" />
          <Tab icon={<VideoIcon />} label="Vídeo" />
        </Tabs>

        {/* Tab de Texto */}
        {tabIndex === 0 && (
          <Box className={classes.tabPanel}>
            <TextField
              label="Digite seu status"
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Digite algo interessante..."
              inputProps={{ maxLength: 700 }}
              helperText={`${textContent.length}/700 caracteres`}
            />

            <FormControl fullWidth className={classes.colorPicker}>
              <InputLabel>Cor de Fundo</InputLabel>
              <Select value={backgroundColor} onChange={(e) => setBackgroundColor(e.target.value)}>
                <MenuItem value="#000000">Preto</MenuItem>
                <MenuItem value="#1a73e8">Azul</MenuItem>
                <MenuItem value="#34a853">Verde</MenuItem>
                <MenuItem value="#ea4335">Vermelho</MenuItem>
                <MenuItem value="#fbbc04">Amarelo</MenuItem>
                <MenuItem value="#9c27b0">Roxo</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Fonte</InputLabel>
              <Select value={font} onChange={(e) => setFont(e.target.value)}>
                <MenuItem value={0}>Padrão</MenuItem>
                <MenuItem value={1}>Monoespaçada</MenuItem>
                <MenuItem value={2}>Serifada</MenuItem>
                <MenuItem value={3}>Casual</MenuItem>
                <MenuItem value={4}>Impacto</MenuItem>
                <MenuItem value={5}>Clássica</MenuItem>
              </Select>
            </FormControl>

            {/* Preview */}
            {textContent && (
              <Box className={classes.previewContainer}>
                <Typography variant="caption" color="textSecondary">
                  Preview:
                </Typography>
                <Box className={classes.textPreview} style={{ backgroundColor }}>
                  {textContent}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {/* Tab de Imagem */}
        {tabIndex === 1 && (
          <Box className={classes.tabPanel}>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileSelect}
            />

            {!mediaPreview ? (
              <Box className={classes.previewContainer}>
                <ImageIcon style={{ fontSize: 60, color: "gray" }} />
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                  Nenhuma imagem selecionada
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PhotoCameraIcon />}
                  className={classes.uploadButton}
                  onClick={() => fileInputRef.current.click()}
                >
                  Selecionar Imagem
                </Button>
              </Box>
            ) : (
              <Box className={classes.previewContainer}>
                <img src={mediaPreview} alt="Preview" className={classes.previewImage} />
                <Button
                  variant="outlined"
                  className={classes.uploadButton}
                  onClick={() => {
                    setMediaFile(null);
                    setMediaPreview(null);
                  }}
                >
                  Trocar Imagem
                </Button>
              </Box>
            )}

            <TextField
              label="Legenda (opcional)"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              style={{ marginTop: 16 }}
              inputProps={{ maxLength: 200 }}
              helperText={`${caption.length}/200 caracteres`}
            />
          </Box>
        )}

        {/* Tab de Vídeo */}
        {tabIndex === 2 && (
          <Box className={classes.tabPanel}>
            <input
              type="file"
              accept="video/*"
              style={{ display: "none" }}
              ref={fileInputRef}
              onChange={handleFileSelect}
            />

            {!mediaPreview ? (
              <Box className={classes.previewContainer}>
                <VideoIcon style={{ fontSize: 60, color: "gray" }} />
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                  Nenhum vídeo selecionado
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PhotoCameraIcon />}
                  className={classes.uploadButton}
                  onClick={() => fileInputRef.current.click()}
                >
                  Selecionar Vídeo
                </Button>
              </Box>
            ) : (
              <Box className={classes.previewContainer}>
                <video src={mediaPreview} controls className={classes.previewVideo} />
                <Button
                  variant="outlined"
                  className={classes.uploadButton}
                  onClick={() => {
                    setMediaFile(null);
                    setMediaPreview(null);
                  }}
                >
                  Trocar Vídeo
                </Button>
              </Box>
            )}

            <TextField
              label="Legenda (opcional)"
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              style={{ marginTop: 16 }}
              inputProps={{ maxLength: 200 }}
              helperText={`${caption.length}/200 caracteres`}
            />
          </Box>
        )}
          </>
        )}
      </DialogContent>

      {!showQRModal && (
        <DialogActions>
          <Button onClick={handleClose} disabled={loading || checkingAuth}>
            Cancelar
          </Button>
          <Button
            onClick={handleSendStatus}
            color="primary"
            variant="contained"
            disabled={loading || checkingAuth}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? "Enviando..." : "Enviar Status"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default StatusModal;
